import axios from 'axios';
import * as S from '../auth.styles';
import { z } from 'zod';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showToast } from 'src/reducers/slices/toast';
import { goToLogin } from 'src/actions/changePassword';
import { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form } from '@design-system/molecules/form';
import { createEmailSchema, EmailField } from '@design-system/molecules/email-field';
import { appConfig } from 'src/config';
import { Link as RouterLink } from 'react-router-dom';

const resetPasswordSchema = z.object({
  email: createEmailSchema(),
});

type FormData = z.infer<typeof resetPasswordSchema>;

const ResetFormContent = (props: { isLoading: boolean }) => {
  const { isLoading } = props;
  const intl = useIntl();
  const { watch } = useFormContext<FormData>();

  const email = watch('email');

  const resetPasswordLabel = intl.formatMessage({ id: 'common.label.email' });
  const resetPasswordButtonLabel = intl.formatMessage({ id: 'common.button.continue' });
  const emailInputHint = intl.formatMessage({ id: 'common.inputHint.email' });

  return (
    <Fragment>
      <S.AuthContainer>
        <EmailField
          name="email"
          label={resetPasswordLabel}
          placeholder="joesmith@gmail.com"
          hintConfig={{ hint: emailInputHint }}
          data-testid="reset-password-email-input"
        />
        <S.BackupSpace />
        <S.AuthButton
          type="submit"
          isLoading={isLoading}
          variant="primary"
          size="large"
          isDisabled={!email?.trim()}
          data-testid="reset-password-submit-button"
        >
          {resetPasswordButtonLabel}
        </S.AuthButton>
        <S.ForgotPasswordContainer>
          <FormattedMessage
            id="components.reset.password.return"
            values={{
              link: (chunks: React.ReactNode) => (
                <S.RecoverLink as={RouterLink} to={'/login'} data-testid="reset-password-back-to-login-link">
                  {chunks}
                </S.RecoverLink>
              ),
            }}
          />
        </S.ForgotPasswordContainer>
      </S.AuthContainer>
    </Fragment>
  );
};

export const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkSent, setIsLinkSent] = useState(false);

  const intl = useIntl();

  const dispatch = useDispatch();

  const handleSubmit = (data: FormData) => {
    resetPassword(data.email);
  };

  const resetPassword = (email: string) => {
    setIsLoading(true);

    const url = `${appConfig.URL_REST}users/reset-password`;
    axios
      .post(url, email, {
        headers: { 'Content-Type': 'text/plain' },
      })
      .then((response) => {
        if (response && response.status === 200) {
          setIsLoading(false);
          dispatch(
            showToast({
              title: intl.formatMessage({ id: 'components.toast.success.title' }),
              message: intl.formatMessage({ id: 'components.reset.password.link.sent' }),
              variant: 'success',
            })
          );
          setIsLinkSent(true);
        }
        if (response && response.status === 404) {
          setIsLoading(false);
          dispatch(
            showToast({
              title: intl.formatMessage({ id: 'components.toast.error.title' }),
              message: intl.formatMessage({ id: 'dialog.change.password.button.reset.password.not.found' }),
              variant: 'error',
            })
          );
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setIsLoading(false);
          dispatch(
            showToast({
              title: intl.formatMessage({ id: 'components.toast.error.title' }),
              message: intl.formatMessage({ id: 'dialog.change.password.button.reset.password.not.found' }),
              variant: 'error',
            })
          );
        } else {
          setIsLoading(false);
          dispatch(
            showToast({
              title: intl.formatMessage({ id: 'components.toast.error.title' }),
              message: intl.formatMessage({ id: 'data.fetching.bad.request' }),
              variant: 'error',
            })
          );
        }
      });
  };

  return (
    <Form onSubmit={handleSubmit} config={{ mode: 'onBlur' }}>
      <S.AuthContainer>
        {isLinkSent ? (
          <>
            <S.ResetPasswordImage src="/reset-link-sent.png" alt="We’ve Sent Recovery Link to Your Email" />
            <S.AuthHeader variant="h2" as="h2">
              <FormattedMessage
                id="components.reset.password.sent.title"
                values={{
                  subTitle: (chunks: React.ReactNode) => (
                    <S.AuthHeaderGreen as="span" variant="h2">
                      {chunks}
                    </S.AuthHeaderGreen>
                  ),
                }}
              />
            </S.AuthHeader>
            <S.AuthSubHeader>
              <FormattedMessage id="components.reset.password.sent.subtitle" />
            </S.AuthSubHeader>
            <S.BackupSpace />
            <S.AuthButton
              type="button"
              variant="primary"
              size="large"
              data-testid="reset-password-return-to-login-button"
              onClick={() => {
                dispatch(goToLogin());
              }}
            >
              <FormattedMessage id="components.reset.password.sent.return" />
            </S.AuthButton>
          </>
        ) : (
          <>
            <S.AuthHeader variant="h2" as="h2">
              <FormattedMessage
                id="components.reset.password.title"
                values={{
                  subTitle: (chunks: React.ReactNode) => (
                    <S.AuthHeaderGreen as="span" variant="h2">
                      {chunks}
                    </S.AuthHeaderGreen>
                  ),
                }}
              />
            </S.AuthHeader>
            <S.AuthSubHeader>
              <FormattedMessage id="components.reset.password.subtitle" />
            </S.AuthSubHeader>
            <ResetFormContent isLoading={isLoading} />
          </>
        )}
      </S.AuthContainer>
    </Form>
  );
};
